import React, { useState, useEffect, useRef } from "react";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import api from "@/lib/api";
import { Send, Sparkles, Mic, StopCircle } from "lucide-react";
import { toast } from "sonner";

export default function AICoachPage() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hello! I'm your AI English Coach. I can play examiner roles, grade essays, drill grammar, or build a study plan. Where would you like to start?" },
  ]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("coach");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    const updated = [...messages, { role: "user", content }];
    setMessages(updated);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/ai/chat", { messages: updated.slice(-12), mode });
      setMessages([...updated, { role: "assistant", content: r.data.reply }]);
    } catch (e) { toast.error("AI unavailable"); }
    finally { setLoading(false); }
  };

  const quickPrompts = [
    "Run a 5-question IELTS Speaking Part 1",
    "Give me a Task 2 essay prompt and feedback as I write",
    "Build me a 14-day plan to reach Band 7.5",
    "Quiz me on collocations for academic writing",
  ];

  return (
    <DashboardLayout title="AI Coach">
      <div className="grid lg:grid-cols-12 gap-6 h-[calc(100vh-180px)]">
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-3xl p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-3">Mode</div>
          {[
            { id: "coach", label: "Friendly Coach" },
            { id: "examiner_ielts", label: "IELTS Examiner" },
            { id: "examiner_pte", label: "PTE Examiner" },
          ].map((m) => (
            <button key={m.id} onClick={() => setMode(m.id)} data-testid={`coach-mode-${m.id}`}
              className={`w-full text-left px-4 py-2.5 rounded-xl mb-1 text-sm transition-all ${
                mode === m.id ? "bg-slate-100 border border-slate-200" : "hover:bg-slate-50 border border-transparent"
              }`}>{m.label}</button>
          ))}
          <div className="mt-6 text-xs font-mono uppercase tracking-widest text-slate-400 mb-3">Quick start</div>
          <div className="space-y-2">
            {quickPrompts.map((p, i) => (
              <button key={i} onClick={() => send(p)} data-testid={`coach-quick-${i}`} className="text-left text-sm text-slate-600 hover:text-slate-900 block">› {p}</button>
            ))}
          </div>
        </div>
        <div className="lg:col-span-9 bg-white border border-slate-200 rounded-3xl flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={`max-w-[80%] ${
                m.role === "user"
                  ? "ml-auto bg-[#0047FF] text-slate-900 rounded-2xl rounded-br-md px-5 py-3"
                  : "bg-slate-50 border border-slate-200 rounded-2xl rounded-bl-md px-5 py-3"
              }`}>
                <p className="leading-relaxed whitespace-pre-wrap">{m.content}</p>
              </div>
            ))}
            {loading && <div className="text-xs text-slate-400 px-2">thinking…</div>}
            <div ref={bottomRef} />
          </div>
          <div className="p-4 border-t border-slate-200 flex gap-2">
            <input data-testid="coach-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask anything..." className="flex-1 bg-[#F6F8FB]/50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#F77F00]" />
            <button onClick={() => send()} data-testid="coach-send" disabled={loading} className="px-5 py-3 rounded-xl bg-[#0047FF] hover:bg-[#00263A] disabled:opacity-50 inline-flex items-center gap-2">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
